import React, { useState, useEffect } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { ShieldCheck, Eye, EyeOff, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";

export default function LoginPage() {
    const navigate = useNavigate();
    const location = useLocation();
    const { login, mfaChallenge, user } = useAuth();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPwd, setShowPwd] = useState(false);
    const [remember, setRemember] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [errors, setErrors] = useState({});
    // MFA second-step state
    const [mfaSessionToken, setMfaSessionToken] = useState(null);
    const [mfaCode, setMfaCode] = useState("");
    const [useBackup, setUseBackup] = useState(false);
    const [mfaSubmitting, setMfaSubmitting] = useState(false);

    useEffect(() => {
        if (user) {
            const to = location.state?.from?.pathname || "/dashboard";
            navigate(to, { replace: true });
        }
    }, [user, location.state, navigate]);

    const validate = () => {
        const e = {};
        if (!email) e.email = "Email richiesta";
        else if (!/^\S+@\S+\.\S+$/.test(email)) e.email = "Email non valida";
        if (!password) e.password = "Password richiesta";
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const onSubmit = async (ev) => {
        ev.preventDefault();
        if (!validate()) return;
        setSubmitting(true);
        try {
            const data = await login(email.trim(), password, remember);
            if (data?.mfa_required) {
                setMfaSessionToken(data.mfa_session_token);
                toast.info("Inserisci il codice a 6 cifre della tua app di autenticazione");
                setSubmitting(false);
                return;
            }
            toast.success("Accesso effettuato");
            const to = location.state?.from?.pathname || "/dashboard";
            navigate(to, { replace: true });
        } catch (err) {
            const detail = err?.response?.data?.detail || "Errore di accesso";
            toast.error(detail);
        } finally {
            setSubmitting(false);
        }
    };

    const onMfaSubmit = async (ev) => {
        ev.preventDefault();
        if (!mfaCode.trim()) { toast.error("Codice obbligatorio"); return; }
        setMfaSubmitting(true);
        try {
            await mfaChallenge(mfaSessionToken, mfaCode.trim(), useBackup);
            toast.success("Accesso effettuato");
            const to = location.state?.from?.pathname || "/dashboard";
            navigate(to, { replace: true });
        } catch (err) {
            const detail = err?.response?.data?.detail || "Codice non valido";
            toast.error(detail);
        } finally {
            setMfaSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen flex bg-[#F5F7FB]">
            {/* Left panel — visual */}
            <div className="hidden lg:flex lg:w-1/2 relative bg-[#0B1633] overflow-hidden">
                <div className="absolute inset-0 opacity-40" style={{
                    backgroundImage:
                        "radial-gradient(circle at 20% 30%, rgba(37,99,235,0.4) 0%, transparent 45%), radial-gradient(circle at 80% 70%, rgba(56,189,248,0.25) 0%, transparent 40%)",
                }} />
                <div className="relative z-10 flex flex-col justify-between p-12 text-white w-full">
                    <div className="flex items-center gap-3">
                        <div className="w-11 h-11 rounded-xl bg-[#2563EB] flex items-center justify-center shadow-xl shadow-[#2563EB]/40">
                            <ShieldCheck className="w-6 h-6" strokeWidth={2.5} />
                        </div>
                        <div>
                            <div className="font-bold text-lg">IH PEC Guard</div>
                            <div className="text-xs text-[#94A3B8] uppercase tracking-wider">Cybersecurity per PEC</div>
                        </div>
                    </div>
                    <div className="space-y-6 max-w-md">
                        <h2 className="text-3xl font-bold leading-tight">
                            Verifica, analizza e proteggi ogni comunicazione PEC.
                        </h2>
                        <p className="text-[#94A3B8] leading-relaxed">
                            Rilevamento avanzato di frodi, phishing e anomalie tecniche su
                            Posta Elettronica Certificata. Strumento pensato per le aziende
                            italiane.
                        </p>
                        <div className="grid grid-cols-3 gap-3 pt-4">
                            {[
                                { k: "Autenticità", v: "Tecnica" },
                                { k: "Rischio", v: "Multi-dim." },
                                { k: "Audit", v: "Immutabile" },
                            ].map((f) => (
                                <div key={f.k} className="bg-white/5 border border-white/10 rounded-lg p-3">
                                    <div className="text-[10px] uppercase tracking-wider text-[#94A3B8]">{f.k}</div>
                                    <div className="text-sm font-semibold mt-1">{f.v}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                    <p className="text-xs text-[#64748B] leading-relaxed max-w-md">
                        Le analisi non costituiscono un parere legale e non sostituiscono la
                        verifica umana.
                    </p>
                </div>
            </div>

            {/* Right — form */}
            <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
                <div className="w-full max-w-md">
                    <div className="lg:hidden flex items-center gap-3 mb-8">
                        <div className="w-10 h-10 rounded-xl bg-[#2563EB] flex items-center justify-center">
                            <ShieldCheck className="w-5 h-5 text-white" strokeWidth={2.5} />
                        </div>
                        <div>
                            <div className="font-bold text-lg text-[#172033]">IH PEC Guard</div>
                        </div>
                    </div>

                    <div className="bg-white border border-[#DCE3EE] rounded-2xl p-8 shadow-sm">
                        {mfaSessionToken ? (
                            <>
                                <h1 className="text-2xl font-bold text-[#172033]">Verifica in due passaggi</h1>
                                <p className="text-sm text-[#64748B] mt-2">
                                    {useBackup
                                        ? "Inserisci uno dei codici di backup salvati durante il setup."
                                        : "Inserisci il codice a 6 cifre generato dalla tua app di autenticazione."}
                                </p>
                                <form onSubmit={onMfaSubmit} className="mt-6 space-y-4" data-testid="mfa-form">
                                    <div>
                                        <Label htmlFor="mfa_code">{useBackup ? "Codice di backup" : "Codice a 6 cifre"}</Label>
                                        <Input
                                            id="mfa_code"
                                            name="mfa_code"
                                            data-testid="mfa-code-input"
                                            autoComplete="one-time-code"
                                            inputMode={useBackup ? "text" : "numeric"}
                                            autoFocus
                                            value={mfaCode}
                                            onChange={(e) => setMfaCode(e.target.value)}
                                            placeholder={useBackup ? "XXXX-XXXX" : "123 456"}
                                            className="h-11 text-lg font-mono tracking-widest text-center"
                                        />
                                    </div>
                                    <label className="flex items-center gap-2 text-sm text-[#172033] cursor-pointer">
                                        <Checkbox
                                            checked={useBackup}
                                            onCheckedChange={(v) => { setUseBackup(!!v); setMfaCode(""); }}
                                            data-testid="mfa-use-backup"
                                        />
                                        Usa un codice di backup
                                    </label>
                                    <Button
                                        type="submit"
                                        disabled={mfaSubmitting}
                                        data-testid="mfa-submit-button"
                                        className="w-full h-11 bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-medium"
                                    >
                                        {mfaSubmitting ? (
                                            <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Verifica in corso…</>
                                        ) : "Verifica e accedi"}
                                    </Button>
                                    <button
                                        type="button"
                                        data-testid="mfa-cancel"
                                        onClick={() => { setMfaSessionToken(null); setMfaCode(""); setUseBackup(false); }}
                                        className="w-full text-xs text-[#64748B] hover:text-[#172033]"
                                    >
                                        ← Torna al login
                                    </button>
                                </form>
                            </>
                        ) : (
                        <>
                        <h1 className="text-2xl font-bold text-[#172033]">Accedi al tuo account</h1>
                        <p className="text-sm text-[#64748B] mt-2">
                            Inserisci le tue credenziali per iniziare.
                        </p>

                        <form onSubmit={onSubmit} className="mt-8 space-y-5" data-testid="login-form">
                            <div>
                                <Label htmlFor="email" className="text-[#172033]">Email</Label>
                                <Input
                                    id="email"
                                    name="email"
                                    type="email"
                                    autoComplete="email"
                                    data-testid="login-email-input"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="nome@azienda.it"
                                    className="mt-1.5 h-11 bg-white border-[#DCE3EE] focus-visible:ring-[#2563EB]"
                                />
                                {errors.email && (
                                    <p className="text-xs text-[#DC2626] mt-1" data-testid="login-email-error">{errors.email}</p>
                                )}
                            </div>

                            <div>
                                <div className="flex items-center justify-between">
                                    <Label htmlFor="password" className="text-[#172033]">Password</Label>
                                    <Link
                                        to="/password-reset"
                                        data-testid="forgot-password-link"
                                        className="text-xs font-medium text-[#2563EB] hover:underline"
                                    >
                                        Password dimenticata?
                                    </Link>
                                </div>
                                <div className="relative mt-1.5">
                                    <Input
                                        id="password"
                                        name="password"
                                        type={showPwd ? "text" : "password"}
                                        autoComplete="current-password"
                                        data-testid="login-password-input"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        placeholder="••••••••"
                                        className="h-11 pr-10 bg-white border-[#DCE3EE] focus-visible:ring-[#2563EB]"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPwd((v) => !v)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748B] hover:text-[#172033]"
                                        aria-label={showPwd ? "Nascondi password" : "Mostra password"}
                                        data-testid="toggle-password-visibility"
                                    >
                                        {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                    </button>
                                </div>
                                {errors.password && (
                                    <p className="text-xs text-[#DC2626] mt-1" data-testid="login-password-error">{errors.password}</p>
                                )}
                            </div>

                            <div className="flex items-center gap-2">
                                <Checkbox
                                    id="remember"
                                    checked={remember}
                                    onCheckedChange={(v) => setRemember(!!v)}
                                    data-testid="remember-me-checkbox"
                                />
                                <label htmlFor="remember" className="text-sm text-[#172033] cursor-pointer">
                                    Ricordami su questo dispositivo
                                </label>
                            </div>

                            <Button
                                type="submit"
                                disabled={submitting}
                                data-testid="login-submit-button"
                                className="w-full h-11 bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-medium"
                            >
                                {submitting ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        Accesso in corso…
                                    </>
                                ) : (
                                    "Accedi"
                                )}
                            </Button>
                        </form>
                        </>
                        )}
                    </div>

                    <p className="text-center text-xs text-[#64748B] mt-6 max-w-md mx-auto">
                        Accedendo, accetti l'utilizzo del servizio secondo i termini di uso
                        aziendale. Le analisi non costituiscono parere legale.
                    </p>
                    <p data-testid="login-credit" className="text-center text-xs text-[#94A3B8] mt-3">
                        Creato da <span className="font-semibold text-[#64748B]">Ptk Solutions</span>
                    </p>
                </div>
            </div>
        </div>
    );
}
